/* eslint-disable @repo/no-style-props, @repo/no-margin-on-root-elements */
import Link from "next/link";
import DocPopup from "@/src/components/layouts/doc-popup";
import { Badge } from "@/src/components/design-system/Badge/Badge";
import {
  type Status,
  StatusBadge,
} from "@/src/components/ui/StatusBadge/StatusBadge";
import { cn } from "@/src/utils/tailwind";

type HeaderProps = {
  title: string;
  titleClassName?: string;
  status?: Status;
  /** Plain informational badge next to the title, e.g. "Demo Org". Not a status. */
  labelBadge?: string;
  label?: {
    text: string;
    href: string;
  };
  help?: { description: string; href?: string; className?: string };
  actionButtons?: React.ReactNode;
  className?: string;
};

export default function Header({ ...props }: HeaderProps) {
  return <BaseHeader {...props} level="h3" />;
}

export function SubHeader({ ...props }: HeaderProps) {
  return <BaseHeader {...props} level="h4" />;
}

export function SubHeaderLabel({ ...props }: HeaderProps) {
  return <BaseHeader {...props} level="h5" />;
}

function HeaderTitle({
  level,
  title,
  className,
}: {
  level: "h3" | "h4" | "h5";
  title: string;
  className?: string;
}) {
  // Top-level titles are bold (the font-relative role) and sit on the
  // primary emphasis tier (the shell's sidebar tint would dim them);
  // sub-levels follow the text-* token weight — hierarchy below h3 is
  // carried by size.
  switch (level) {
    case "h3":
      return (
        <h3
          className={cn("text-primary text-lg leading-7 font-bold", className)}
        >
          {title}
        </h3>
      );
    case "h4":
      return <h4 className={cn("text-lg", className)}>{title}</h4>;
    case "h5":
      return <h5 className={cn("text-base", className)}>{title}</h5>;
  }
}

function BaseHeader({ ...props }: HeaderProps & { level: "h3" | "h4" | "h5" }) {
  return (
    <div className={cn(props.className, props.level === "h3" && "mb-2")}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3 md:gap-5">
          <div className="flex min-w-0 flex-row items-center">
            <HeaderTitle
              title={props.title}
              level={props.level}
              className={props.titleClassName}
            />
            {props.help ? (
              <DocPopup
                description={props.help.description}
                href={props.help.href}
                className={props.help.className}
              />
            ) : null}
          </div>
          {props.status && <StatusBadge type={props.status} />}
          {props.labelBadge && (
            <Badge
              color="filled"
              font="mono"
              size="sm"
              text={props.labelBadge}
            />
          )}
          {props.label && (
            <Link href={props.label.href} className="flex">
              <Badge
                color="filled"
                font="mono"
                size="sm"
                text={props.label.text}
              />
            </Link>
          )}
        </div>
        {props.actionButtons ? (
          <div className="flex flex-wrap items-center gap-3">
            {props.actionButtons}
          </div>
        ) : null}
      </div>
    </div>
  );
}
